import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { CaseOverviewOut } from "@/api";
import { HandoverMark } from "@/components/stages/HandoverMark";
import { StageChip } from "@/components/stages/StageChip";
import { VolumeFigure } from "@/components/VolumeFigure";
import { activityWord } from "@/lib/activity";
import { extraWords, gapsInWords } from "@/lib/extraWords";
import { activityMoment, formatDateTime } from "@/lib/format";

import styles from "./CaseCells.module.css";

/**
 * One Vorgang, one row grammar (UX gate round 2, #1). „Zuletzt bearbeitet"
 * on the start page and the register were two components for the same
 * object — different cells, a day against a clock time, a title at 15 px
 * against 13. These are the cells both are built from: the same five in the
 * same order, two lines each, the first saying what stands out, the second
 * what qualifies it. Each list only decides how the cells are laid out (a
 * table row, a link across a grid, a card) — never what a cell says or how
 * it is set.
 *
 * Spans only: the start page puts them inside one `<a>` per row, the
 * register inside `<td>`s.
 */

type Item = CaseOverviewOut;

/** The customer as a list names them: the company, else the person. */
export function customerNameOf(item: Pick<Item, "customer">): string | null {
  const customer = item.customer;
  return customer !== null ? (customer.company ?? customer.displayName) : null;
}

/**
 * „Vorgang": the title on the first line, the case number on the second.
 * `title` replaces the plain first line — the register's is a link. After
 * the number the customer waits, hidden, for the width at which a list folds
 * its „Kunde" column away (`[data-case-customer]`, each list's own tier).
 */
/**
 * „kein Kunde" as a way to give it one (UX gate round 4, #13): a link to the
 * record with the customer dialog asked for (`?kunde=1`). Only where the row
 * is not itself one link — the register's, not the start page's.
 */
function NoCustomer({ href, describedBy, className }: { href?: string; describedBy?: string; className: string }) {
  if (href === undefined) return <>kein Kunde</>;
  return (
    // The name as a label, not a hidden suffix: a hidden text node lost its
    // separating space in the name computation (`RegisterTable`'s `SortHead`).
    // It starts with the visible words (WCAG 2.5.3).
    <Link
      to={href}
      className={`${className} ${styles.assign}`}
      aria-label="kein Kunde – zuordnen"
      aria-describedby={describedBy}
      title="Kunde zuordnen"
    >
      kein Kunde
    </Link>
  );
}

export function CaseWhat({
  item,
  title,
  assignHref,
  describedBy,
}: {
  item: Item;
  title?: ReactNode;
  /** Where „kein Kunde" leads, if it is a link (`NoCustomer`). */
  assignHref?: string;
  describedBy?: string;
}) {
  const name = customerNameOf(item);
  // The whole of the second line in `title`: at the folded width it carries
  // the customer too, and an ellipsis may cut them.
  const whole = [item.caseNo, name ?? "kein Kunde", item.customer?.email ?? null]
    .filter((part): part is string => part !== null && part !== "")
    .join(" · ");
  return (
    <>
      {title ?? (
        <span className={`${styles.line1} ${styles.title}`} title={item.title}>
          {item.title}
        </span>
      )}
      <span className={styles.line2} title={whole} data-case-line="">
        <span className={`num ${styles.no}`}>
          {item.caseNo}
        </span>
        <span className={styles.caseCustomer} data-case-customer="">
          {" · "}
          {name ?? <NoCustomer href={assignHref} describedBy={describedBy} className="" />}
        </span>
      </span>
    </>
  );
}

/** Line-1 class for a caller that draws its own title (the register's link). */
export const caseTitleClass = `${styles.line1} ${styles.title}`;

/** The two line boxes, for a list's loading skeleton: a bone in them is exactly a line of text tall. */
export const caseLineClass = { one: styles.line1 ?? "", two: styles.line2 ?? "" } as const;

/**
 * „Kunde": the name — „kein Kunde" in quiet italics, never a dash (S-10),
 * and in the register a way to give it one (`assignHref`, UX gate round 4,
 * #13) — and, where asked, the address.
 */
export function CaseCustomer({
  item,
  email = false,
  assignHref,
  describedBy,
}: {
  item: Item;
  email?: boolean;
  assignHref?: string;
  describedBy?: string;
}) {
  const name = customerNameOf(item);
  const address = item.customer?.email ?? null;
  return (
    <>
      {name === null && assignHref !== undefined ? (
        <NoCustomer href={assignHref} describedBy={describedBy} className={`${styles.line1} ${styles.none}`} />
      ) : (
        <span className={`${styles.line1} ${name === null ? styles.none : ""}`} title={name ?? undefined}>
          {name ?? "kein Kunde"}
        </span>
      )}
      {email ? (
        <span className={styles.line2} title={address ?? undefined}>
          {address ?? ""}
        </span>
      ) : null}
    </>
  );
}

/** The whole sentence for the hover and the screen reader (as `HandoverMark` says it). */
function handoverSentence(toName: string): string {
  return `Übergabe an ${toName} angefragt`;
}

/**
 * „Stadium": the chip, the handover as a mark beside it, and the words under
 * it — the gaps or the release („mit n Lücken", „freigegeben mit n Lücken",
 * D-24), the stage's note („Freigabe offen", „Preis fehlt"), and an open
 * handover's recipient, „an Chiara Muster" (UX gate round 4, #6: „Übergabe
 * angefragt" did not say to whom — the name was on a hover). The release
 * comes first and the name after it, so where the column is too narrow for
 * both the ellipsis takes the name, never the release; the whole of the line
 * is in `title`. The mark beside the chip is decorative: the words speak,
 * and the screen reader hears the whole sentence from them once.
 */
export function CaseStage({ item }: { item: Item }) {
  const handover = item.pendingHandover;
  const words = extraWords({ ...item, pendingHandover: null });
  const sentence = handover !== null ? handoverSentence(handover.toDisplayName) : null;
  const whole = [...words, ...(sentence !== null ? [sentence] : [])].join(" · ");
  return (
    <>
      <span className={styles.line1}>
        <StageChip status={item.status} />
        {handover !== null ? <HandoverMark toName={handover.toDisplayName} decorative /> : null}
      </span>
      <span className={`${styles.line2} ${styles.extra}`} title={whole !== "" ? whole : undefined} data-stage-words="">
        {words.map((word, index) => (
          <span key={word}>
            {index > 0 ? <span aria-hidden="true"> · </span> : null}
            <span>{word}</span>
          </span>
        ))}
        {handover !== null && sentence !== null ? (
          <span data-handover-word="">
            {words.length > 0 ? <span aria-hidden="true"> · </span> : null}
            <span aria-hidden="true">an {handover.toDisplayName}</span>
            <span className="visually-hidden">{sentence}</span>
          </span>
        ) : null}
      </span>
    </>
  );
}

/**
 * „Volumen": the amount in mono with its gap count, right-aligned. With no
 * amount a dash under a column head that says what the column is, and the
 * words where no head does (R2-12). `both` draws the two and leaves the
 * choice to the list's own tiers — the register's head goes when its rows
 * turn into cards (`[data-missing]`). The gap count only where the stage's
 * words beside it do not already count them (UX gate round 4, #2).
 */
export function CaseVolume({ item, missing }: { item: Item; missing: "dash" | "words" | "both" }) {
  return <VolumeFigure volume={item.volume} missing={missing} gaps={!gapsInWords(item)} status={item.status} />;
}

/**
 * „Zuletzt": when, as the register reads it — „heute 11:24", „Mo 16:02",
 * „12.09." —; under it what happened then. The digits as tabular figures in
 * the UI face (UX gate round 4, #8; they were mono since register fix round
 * 1, M7): a time is no measure, and they still stand under each other.
 */
export function CaseWhen({ item }: { item: Item }) {
  const moment = activityMoment(item.lastActivityAt);
  const what = activityWord(item.lastActivityKind);
  return (
    <>
      <time className={styles.line1} dateTime={item.lastActivityAt} title={formatDateTime(item.lastActivityAt)}>
        {moment.word !== null ? `${moment.word} ` : null}
        <span className="tabular">{moment.digits}</span>
      </time>
      <span className={styles.line2} title={what}>
        {what}
      </span>
    </>
  );
}
